import React from 'react';
import ReportSummary from './ReportSummary';
import { ReportData } from '../../types/interfaces';
import Loader from '../Loader';
import { useTranslation } from 'react-i18next';
import defaultDamelioLogo from '../../assets/damelio_logo_negro.png';
import unknownLogo from '../../assets/unknown_logo.png';

interface ReportSummarySliderProps {
  reports: ReportData[];
  selectedReportId: string | null;
  onSelectReport: (reportId: string | null) => void;
  reportSummary: ReportData | null;
  loading: boolean;
  error: string;
}


function getFinalLogo(url?: string | null): string {
  // 1. Si 'url' === 'damelio', usar el logo local de Damelio
  if (url === 'damelio') {
    return defaultDamelioLogo;
  }

  // 2. Si 'url' está vacío, null, undefined o solo espacios
  if (!url || url.trim() === '') {
    return unknownLogo;
  }

  // 3. En cualquier otro caso, se asume que es una URL
  return url;
}

const ReportSummarySlider: React.FC<ReportSummarySliderProps> = ({
  reports,
  selectedReportId,
  onSelectReport,
  reportSummary,
  loading,
  error,
}) => {
  const { t } = useTranslation();

  // 1. Si está cargando, mostramos Loader
  if (loading) {
    return <Loader />;
  }

  // 2. Si hay un error, lo mostramos
  if (error) {
    return <p className="text-red-500">{error}</p>;
  }

  // 3. Identificamos el reporte seleccionado (si existe)
  const selectedReport = selectedReportId
    ? reports.find((report) => report.id === selectedReportId)
    : null;

  // 4. Obtenemos la URL a mostrar (si el 'selectedReport' no existe, usaremos 'reportSummary' o viceversa)
  const logoUrl = getFinalLogo(
    selectedReport?.company_logo_url ?? reportSummary?.company_logo_url
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
      <h2 className="text-3xl font-bold mb-4">
        {t('reportSummarySlider.title')}
      </h2>

      <div className="flex items-center mb-4">
        {/* Select de informes */}
        <div className="flex-grow">
          <label
            htmlFor="report-select"
            className="block text-xl font-medium text-gray-700 mb-2"
          >
            {t('reportSummarySlider.selectReportLabel')}
          </label>
          <select
            id="report-select"
            value={selectedReportId ?? ''}
            onChange={(e) =>
              onSelectReport(e.target.value === '' ? null : e.target.value)
            }
            className="block w-full max-w-md mt-1 p-2 border border-gray-300 rounded-md shadow-sm
                       bg-gray-800 text-white font-bold focus:bg-gray-700 
                       hover:border-gray-500 focus:ring-2 focus:ring-gray-500"
          >
            <option value="" className="text-gray-300 font-bold">
              {t('reportSummarySlider.selectReportOption')}
            </option>
            {reports.map((report) => (
              <option
                key={report.id}
                value={report.id}
                className="text-white font-bold"
              >
                {report.company_name || `Informe ${report.id}`}
              </option>
            ))}
          </select>
        </div>

        {/* Logo */}
        <div className="ml-4">
          <img
            src={logoUrl}
            alt={t('reportSummarySlider.companyLogoAlt')}
            className="h-20 w-auto max-w-full object-contain"
            // Si falla la carga de la URL, fallback a 'unknownLogo'
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = unknownLogo;
            }}
          />
        </div>
      </div>

      {/* Mostrar el componente de resumen */}
      <ReportSummary summaryData={reportSummary} />
    </div>
  );
};

export default ReportSummarySlider;
